import React, { useEffect, useMemo, useState } from "react";
import { FaImage, FaSave, FaTimes, FaUpload } from "react-icons/fa";

import {
  REGLEMENT_FALLBACK_IMAGE,
  REGLEMENT_UPLOAD_TARGETS,
} from "../utils/reglementInterieurUtils";

function SkeletonCard() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="grid gap-4 p-5 lg:grid-cols-3">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="h-64 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function ImageUploadCard({
  target,
  currentImage,
  selectedFile,
  isUploading,
  disabled,
  onFileSelect,
  onFileClear,
  onUpload,
}) {
  const [localPreview, setLocalPreview] = useState("");

  const config = REGLEMENT_UPLOAD_TARGETS[target];

  useEffect(() => {
    if (!selectedFile) {
      setLocalPreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const previewSrc = useMemo(() => {
    return localPreview || currentImage || REGLEMENT_FALLBACK_IMAGE;
  }, [localPreview, currentImage]);

  return (
    <article
      className={`rounded-[24px] border border-slate-200 bg-slate-50 p-4 transition dark:border-slate-800 dark:bg-slate-950/70 ${
        disabled ? "pointer-events-none opacity-60" : ""
      }`}
    >
      <div className="mb-3">
        <h4 className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-white">
          <FaImage className="text-indigo-600 dark:text-indigo-300" />
          {config.label}
        </h4>

        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {config.hint}
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <img
          src={previewSrc}
          alt={`${config.label} — aperçu`}
          className="h-44 w-full object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          onError={(event) => {
            event.currentTarget.src = REGLEMENT_FALLBACK_IMAGE;
          }}
        />
      </div>

      <div className="mt-4">
        <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
          Sélectionner une image
        </label>

        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={(event) =>
            onFileSelect(target, event.target.files?.[0] || null)
          }
          disabled={isUploading}
          className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
        />

        {selectedFile ? (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {selectedFile.name} — {Math.round(selectedFile.size / 1024)} Ko
          </p>
        ) : null}
      </div>

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {selectedFile ? (
          <button
            type="button"
            onClick={() => onFileClear(target)}
            disabled={isUploading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => onUpload(target)}
          disabled={!selectedFile || isUploading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
        >
          {isUploading ? <FaUpload className="animate-pulse" /> : <FaSave />}
          {isUploading ? "Transfert..." : "Sauvegarder"}
        </button>
      </div>
    </article>
  );
}

export default function ReglementImagesCard({
  loading,
  previewUrls,
  selectedFiles,
  uploadingTarget,
  disabledUpload,
  onFileSelect,
  onFileClear,
  onUpload,
}) {
  if (loading) return <SkeletonCard />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Visuels
          </p>

          <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            <FaImage className="text-indigo-600 dark:text-indigo-300" />
            Images du règlement
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Gérez les trois images utilisées pour mettre en valeur le règlement
            intérieur : bannière, première illustration et seconde illustration.
          </p>
        </div>

        <div className="grid gap-4 xl:grid-cols-3">
          <ImageUploadCard
            target="header"
            currentImage={previewUrls.header}
            selectedFile={selectedFiles.header}
            isUploading={uploadingTarget === "header"}
            disabled={disabledUpload && uploadingTarget !== "header"}
            onFileSelect={onFileSelect}
            onFileClear={onFileClear}
            onUpload={onUpload}
          />

          <ImageUploadCard
            target="first"
            currentImage={previewUrls.first}
            selectedFile={selectedFiles.first}
            isUploading={uploadingTarget === "first"}
            disabled={disabledUpload && uploadingTarget !== "first"}
            onFileSelect={onFileSelect}
            onFileClear={onFileClear}
            onUpload={onUpload}
          />

          <ImageUploadCard
            target="second"
            currentImage={previewUrls.second}
            selectedFile={selectedFiles.second}
            isUploading={uploadingTarget === "second"}
            disabled={disabledUpload && uploadingTarget !== "second"}
            onFileSelect={onFileSelect}
            onFileClear={onFileClear}
            onUpload={onUpload}
          />
        </div>
      </div>
    </section>
  );
}